import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { superAdminApi } from '../services/super-admin-api'
import type { ApiResponse } from '../services/super-admin-api'

export interface SuperAdminPermissions {
  can_manage_organizations: boolean
  can_manage_billing: boolean
  can_manage_plans: boolean
  can_manage_users: boolean
  can_impersonate: boolean
  can_access_audit_logs: boolean
  can_manage_config: boolean
}

export interface SuperAdmin {
  id: string
  profile_id: string
  email: string
  name: string | null
  created_at: string
  updated_at: string
  permissions: SuperAdminPermissions
}

export function useSuperAdmins() {
  return useQuery({
    queryKey: ['super-admin', 'super-admins'],
    queryFn: async (): Promise<SuperAdmin[]> => {
      const res = await superAdminApi.get<ApiResponse<SuperAdmin[]>>('/super-admins')
      return res.data
    },
  })
}

export function usePromoteSuperAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { profile_id: string; permissions?: Partial<SuperAdminPermissions> }) => {
      const res = await superAdminApi.post<ApiResponse<SuperAdmin>>('/super-admins/promote', payload)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['super-admin', 'super-admins'] }),
  })
}

export function useCreateSuperAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      email: string
      full_name: string
      permissions?: Partial<SuperAdminPermissions>
    }) => {
      const res = await superAdminApi.post<{ data: SuperAdmin; temporaryPassword: string }>('/super-admins/create', payload)
      return res as { data: SuperAdmin; temporaryPassword: string }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['super-admin', 'super-admins'] }),
  })
}

export function useUpdateSuperAdminPermissions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, permissions }: { id: string; permissions: Partial<SuperAdminPermissions> }) => {
      const res = await superAdminApi.patch<ApiResponse<SuperAdmin>>(`/super-admins/${id}/permissions`, permissions)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['super-admin', 'super-admins'] }),
  })
}

export function useRevokeSuperAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await superAdminApi.delete(`/super-admins/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['super-admin', 'super-admins'] }),
  })
}
